import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from '../lib/auth';
import SkipLink from './SkipLink';

// Navigation items for different user roles
const dashboardItems = [
    { to: '/app', label: 'Dashboard', icon: '🏠', end: true },
    { to: '/app/player', label: 'Player Hub', icon: '🏴‍☠️' },
    { to: '/app/ladder', label: 'My Ladder', icon: '⚔️' },
    { to: '/app/techniques', label: 'Techniques', icon: '📚' },
    { to: '/app/report-bug', label: 'Report Bug', icon: '🐛' },
];

const adminItems = [
    { to: '/app/admin/events', label: 'Events', icon: '📅' },
    { to: '/app/admin/teams', label: 'Teams', icon: '👥' },
    { to: '/app/admin/ladder', label: 'SSBU Ladder', icon: '⚔️' },
    { to: '/app/admin/vods', label: 'VoDs', icon: '📹' },
    { to: '/app/admin/opponents', label: 'Opponents', icon: '🎯' },
    { to: '/app/admin/roster', label: 'Roster', icon: '📋' },
    { to: '/app/admin/discord', label: 'Discord', icon: '💬' },
    { to: '/app/admin/permission-slips', label: 'Permission Slips', icon: '📝' },
    { to: '/app/admin/documents', label: 'Documents', icon: '📁' },
    { to: '/app/admin/records', label: 'Records', icon: '📊' },
    { to: '/app/admin/bug-reports', label: 'Bug Reports', icon: '🐛' },
];

export default function AuthenticatedLayout() {
    const { user, userDoc, loading, onboardingCompleted, isUnder13 } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const isAdmin = userDoc?.role === 'admin' || userDoc?.role === 'coach';

    // Check authentication and onboarding status
    useEffect(() => {
        if (!loading && !user) {
            navigate('/login');
            return;
        }

        // Redirect to onboarding if not completed (for all users, including admins for demo)
        if (!loading && user && userDoc && !onboardingCompleted) {
            navigate('/onboarding');
            return;
        }

        // Block under-13 users from authenticated area
        if (!loading && user && userDoc && isUnder13) {
            navigate('/onboarding');
            return;
        }
    }, [user, userDoc, loading, onboardingCompleted, isUnder13, navigate]);

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-secondary-800">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    const NavItem = ({ item, onClick }: { item: { to: string; label: string; icon: string; end?: boolean }, onClick?: () => void }) => (
        <NavLink
            to={item.to}
            end={item.end}
            onClick={onClick}
            className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive
                    ? 'bg-primary-500 text-white'
                    : 'text-secondary-300 hover:bg-secondary-800 hover:text-white'
                }
            `}
        >
            <span className="text-lg">{item.icon}</span>
            {item.label}
        </NavLink>
    );

    return (
        <div className="min-h-screen flex bg-secondary-800">
            <SkipLink />
            {/* Mobile sidebar toggle */}
            <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden fixed bottom-4 right-4 z-50 w-14 h-14 bg-primary-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-600 transition-colors"
                aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={sidebarOpen}
                aria-controls="sidebar-navigation"
            >
                <span className="text-xl">{sidebarOpen ? '✕' : '☰'}</span>
            </button>

            {/* Sidebar overlay (mobile) */}
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/50 z-30"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                id="sidebar-navigation"
                aria-label="Dashboard navigation"
                className={`
                    fixed lg:sticky top-0 left-0 h-screen w-64 
                    bg-secondary-900 border-r border-secondary-700 
                    transform transition-transform duration-300 z-40
                    flex flex-col
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}
            >
                {/* Logo/Header */}
                <div className="p-4 border-b border-secondary-700">
                    <Link to="/app" className="flex items-center gap-3 group">
                        <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                            <span className="text-white text-xl">🏴‍☠️</span>
                        </div>
                        <div>
                            <span className="font-display font-bold text-lg text-white block">Pirates</span>
                            <span className="text-xs text-secondary-400">Esports Dashboard</span>
                        </div>
                    </Link>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-6 overflow-y-auto" aria-label="Dashboard menu">
                    {/* Dashboard Section */}
                    <div>
                        <p className="px-3 text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-2">
                            Dashboard
                        </p>
                        <div className="space-y-1">
                            {dashboardItems.map(item => (
                                <NavItem key={item.to} item={item} onClick={() => setSidebarOpen(false)} />
                            ))}
                        </div>
                    </div>

                    {/* Admin Section - Only show for admins/coaches */}
                    {isAdmin && (
                        <div>
                            <p className="px-3 text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-2">
                                Admin Tools
                            </p>
                            <div className="space-y-1">
                                {adminItems.map(item => (
                                    <NavItem key={item.to} item={item} onClick={() => setSidebarOpen(false)} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quick Links */}
                    <div>
                        <p className="px-3 text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-2">
                            Quick Links
                        </p>
                        <div className="space-y-1">
                            <Link
                                to="/"
                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-secondary-300 hover:bg-secondary-800 hover:text-white transition-colors"
                                onClick={() => setSidebarOpen(false)}
                            >
                                <span className="text-lg">🌐</span>
                                View Public Site
                            </Link>
                        </div>
                    </div>
                </nav>

                {/* User info at bottom */}
                <div className="p-4 border-t border-secondary-700">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                            <span className="text-primary-400">👤</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                                {userDoc?.displayName || user?.displayName || user?.email?.split('@')[0]}
                            </p>
                            <p className="text-xs text-secondary-400 capitalize">{userDoc?.role || 'User'}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleSignOut}
                        className="w-full px-3 py-2 text-sm font-medium text-secondary-300 hover:text-white hover:bg-secondary-800 rounded-lg transition-colors text-left flex items-center gap-2"
                    >
                        <span>🚪</span>
                        Sign Out
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <main id="main-content" className="flex-1 min-w-0" tabIndex={-1}>
                <Outlet />
            </main>
        </div>
    );
}
